import apiFetch from './client';
import type { Condominium, ParkingSpot } from './condominiums';
import type { SpotUsage, UsageType } from './my-parking-spots';

export interface AvailabilityWindowInfo {
  id: string;
  startAt: string;
  endAt: string;
  allowBorrow: boolean;
  allowRent: boolean;
}

export interface AvailableSpot {
  id: string;
  code: string;
  location: string | null;
  level: string | null;
  blockId: string;
  blockName: string;
  blockCode: string | null;
  isMine?: boolean;
  availabilityWindows: AvailabilityWindowInfo[];
  locationX?: number;
  locationY?: number;
  locationWidth?: number;
  locationHeight?: number;
}

export interface GetAvailableSpotsParams {
  startAt?: string;
  endAt?: string;
  allowBorrow?: boolean;
  allowRent?: boolean;
}

export interface CreateSpotUsageRequest {
  availabilityWindowId: string;
  startAt: string;
  endAt: string;
  type: UsageType;
}

export interface BulkRequestPayload {
  startAt: string;
  endAt: string;
  type: UsageType;
}

export interface BulkRequestResponse {
  createdCount: number;
}

export async function getMyCondominiums(): Promise<Condominium[]> {
  const data = await apiFetch<Condominium[]>('/condominiums/my-condominiums');
  return data ?? [];
}

export async function getAllCondominiums(): Promise<Condominium[]> {
  const data = await apiFetch<Condominium[]>('/condominiums');
  return data ?? [];
}

export async function getAvailableSpots(
  condominiumId: string,
  params?: GetAvailableSpotsParams,
): Promise<AvailableSpot[]> {
  const queryParams = new URLSearchParams();
  if (params?.startAt) queryParams.append('startAt', params.startAt);
  if (params?.endAt) queryParams.append('endAt', params.endAt);
  if (params?.allowBorrow !== undefined) {
    queryParams.append('allowBorrow', String(params.allowBorrow));
  }
  if (params?.allowRent !== undefined) {
    queryParams.append('allowRent', String(params.allowRent));
  }

  const queryString = queryParams.toString();
  const url = `/condominiums/${condominiumId}/available-spots${queryString ? `?${queryString}` : ''}`;
  const data = await apiFetch<AvailableSpot[]>(url);
  return data ?? [];
}

export async function getAllSpotsByCondominium(condominiumId: string): Promise<ParkingSpot[]> {
  const data = await apiFetch<ParkingSpot[]>(`/condominiums/${condominiumId}/all-spots`);
  return data ?? [];
}

export async function createSpotUsage(request: CreateSpotUsageRequest): Promise<SpotUsage> {
  return apiFetch<SpotUsage>('/spot-usages', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function createBulkSpotUsageRequest(
  request: BulkRequestPayload,
): Promise<BulkRequestResponse> {
  return apiFetch<BulkRequestResponse>('/spot-usages/bulk-request', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function createSpotOwnerRequest(
  request: BulkRequestPayload & { spotId: string },
): Promise<BulkRequestResponse> {
  return apiFetch<BulkRequestResponse>('/spot-usages/spot-request', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export const availableSpotsKeys = {
  all: ['available-spots'] as const,
  condominiums: (isGlobalAdmin: boolean) =>
    [...availableSpotsKeys.all, 'condominiums', isGlobalAdmin] as const,
  available: (condominiumId: string, filters: GetAvailableSpotsParams) =>
    [...availableSpotsKeys.all, 'available', condominiumId, filters] as const,
  allSpots: (condominiumId: string) =>
    [...availableSpotsKeys.all, 'all-spots', condominiumId] as const,
  myUsages: () => [...availableSpotsKeys.all, 'my-usages'] as const,
};
